// src/pages/Admin/SharePointLibrariesPage.tsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import {
  getGlobalSharePointSettings,
  updateGlobalSharePointSettings,
  GlobalSharePointSettings,
  SharePointLibraryApiError,
  UpdateGlobalSharePointSettingsRequest,
} from '../../services/sharePointLibraryApi';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../services/authApi';

export function SharePointLibrariesPage() {
  const { user } = useAuth();
  const [_settings, setSettings] = useState<GlobalSharePointSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Only System Admin can manage settings
  const canEditSettings = user && hasRole(user, 'System Admin');
  
  // Form state
  const [formData, setFormData] = useState<UpdateGlobalSharePointSettingsRequest>({
    libraryName: '',
    siteId: '',
    webUrl: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Load global settings
  const loadSettings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getGlobalSharePointSettings();
      setSettings(response);
      setFormData({
        libraryName: response.libraryName || '',
        siteId: response.siteId || '',
        webUrl: response.webUrl || '',
      });
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to load SharePoint settings');
      console.error('Error loading SharePoint settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.libraryName || formData.libraryName.trim() === '') {
      errors.libraryName = 'Library name is required';
    }
    
    if (!formData.siteId || formData.siteId.trim() === '') {
      errors.siteId = 'Site ID is required';
    }
    
    // Validate web URL format if provided
    if (formData.webUrl && formData.webUrl.trim() !== '') {
      try {
        new URL(formData.webUrl);
      } catch {
        errors.webUrl = 'Invalid URL format';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      await updateGlobalSharePointSettings(formData);
      setSuccessMessage('SharePoint settings updated successfully');
      await loadSettings(); // Reload to get updated values
    } catch (err) {
      const apiError = err as SharePointLibraryApiError;
      setError(apiError.message || 'Failed to update SharePoint settings');
      console.error('Error updating SharePoint settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white' }}>
          SharePoint Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Global SharePoint Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the system-wide SharePoint library settings. These settings will be used for all projects.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Library Name"
              value={formData.libraryName}
              onChange={(e) => {
                setFormData({ ...formData, libraryName: e.target.value });
                setFormErrors({ ...formErrors, libraryName: '' });
              }}
              error={!!formErrors.libraryName}
              helperText={formErrors.libraryName || 'Name of the SharePoint document library'}
              required
              fullWidth
              disabled={!canEditSettings || isSaving}
            />

            <TextField
              label="Site ID"
              value={formData.siteId}
              onChange={(e) => {
                setFormData({ ...formData, siteId: e.target.value });
                setFormErrors({ ...formErrors, siteId: '' });
              }}
              error={!!formErrors.siteId}
              helperText={formErrors.siteId || 'SharePoint site ID (GUID)'}
              required
              fullWidth
              disabled={!canEditSettings || isSaving}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />

            <TextField
              label="Web URL"
              value={formData.webUrl || ''}
              onChange={(e) => {
                setFormData({ ...formData, webUrl: e.target.value });
                setFormErrors({ ...formErrors, webUrl: '' });
              }}
              error={!!formErrors.webUrl}
              helperText={formErrors.webUrl || 'Optional: SharePoint site web URL'}
              fullWidth
              disabled={!canEditSettings || isSaving}
            />

            {canEditSettings && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
